/**
 * Sothiares Custom JavaScript — Homepage Scroll Reveal
 * ====================================================
 * Adds a staged fade-up reveal to homepage sections as they enter the
 * viewport, for a smoother, more "considered" feel (ref: Rose & Born;
 * vault: Projects/website-polish). Pairs with custom-scroll-reveal.css.
 *
 * Author: Sothiares team (via Claude) — 2026-07-09
 * DRAFT: scoped to the homepage only for preview. No core theme files touched.
 *
 * Design notes:
 * - The hidden state is set in CSS and gated on <html>.so-reveal-ready (added
 *   synchronously in theme.liquid <head>), so there is no flash. This file only
 *   adds `.is-revealed` to reveal each section.
 * - Honors prefers-reduced-motion: if the user opts out, we bail and the CSS
 *   never hides anything, so all content is visible immediately.
 * - Progressive enhancement: if IntersectionObserver is unavailable, we reveal
 *   everything at once.
 * - The first (hero) section reveals immediately so above-the-fold never waits.
 *
 * Trigger geometry (2026-09-01):
 * - This used to observe with `threshold: 0.12`, which is a fraction of the
 *   TARGET, not of the viewport. That silently breaks for any section taller
 *   than roughly 8x the viewport: on /collections/all-products the whole
 *   product grid is one 6307px section, so against an ~834px effective root its
 *   intersectionRatio maxes out at ~0.13 and sat at 0.107 on load. The 0.12
 *   threshold was never reliably crossed, `.is-revealed` was never added, and
 *   the page rendered blank. Taller grid or shorter viewport (i.e. mobile) made
 *   it worse.
 * - Now the trigger is a LINE 12% up from the bottom of the viewport, which the
 *   section's top edge crosses. Timing for normal-height sections is
 *   effectively unchanged, but it is height-independent and cannot be missed.
 * - A failsafe reveals anything still hidden after REVEAL_FAILSAFE_MS. A fade
 *   that fires late is a cosmetic problem; a shop page that never appears is a
 *   revenue problem, so the failsafe always wins.
 */

(function () {
  'use strict';

  // Longest we will ever leave a section hidden waiting on the observer.
  var REVEAL_FAILSAFE_MS = 2000;

  var main = document.getElementById('MainContent');
  if (!main) return;

  // Home + collection pages (data-template is "index" / "collection" / "*.context.*").
  var template = main.getAttribute('data-template') || '';
  if (template.indexOf('index') !== 0 && template.indexOf('collection') !== 0) return;

  // Respect reduced-motion — the CSS hidden state is already disabled for these
  // users; make sure JS does nothing either.
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  function revealAll(sections) {
    for (var i = 0; i < sections.length; i++) sections[i].classList.add('is-revealed');
  }

  function init() {
    var sections = main.querySelectorAll(':scope > .shopify-section');
    if (!sections.length) return;

    // No IntersectionObserver → just show everything (still gets the CSS
    // transition, but no scroll gating).
    if (!('IntersectionObserver' in window)) {
      revealAll(sections);
      return;
    }

    // Reveal the first section right away so the hero never waits on scroll.
    if (sections[0]) sections[0].classList.add('is-revealed');

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      // threshold 0 + a negative bottom margin = "reveal once the top edge
      // crosses a line 12% above the fold", independent of section height.
      // See the header note: a ratio threshold here left tall grids invisible.
      { threshold: 0, rootMargin: '0px 0px -12% 0px' }
    );

    for (var i = 1; i < sections.length; i++) observer.observe(sections[i]);

    // Failsafe: never leave content hidden because a reveal did not fire.
    window.setTimeout(function () {
      for (var j = 1; j < sections.length; j++) {
        if (!sections[j].classList.contains('is-revealed')) {
          sections[j].classList.add('is-revealed');
          observer.unobserve(sections[j]);
        }
      }
    }, REVEAL_FAILSAFE_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
